/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FaxDevicesPage.tsx
 🗓️ Created: 2025-12-03
 🧩 Description:
    Main page for Fax Devices management with list view
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, Plus, Edit, Trash2, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useFaxDeviceList } from './hooks/useFaxDeviceList';
import { deleteFaxDevice } from './api/faxDeviceApi';
import type { FaxDevice } from './types';

export default function FaxDevicesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; device: FaxDevice | null }>({
    isOpen: false,
    device: null
  });

  // Define columns with custom renders
  const availableColumns = useMemo(() => [
    {
      key: 'name',
      label: t('faxDevices:columns.name'),
      sortable: true,
      visible: true,
      render: (_value: unknown, device: FaxDevice) => (
        <span className="font-medium text-foreground">{device.name}</span>
      ),
    },
    {
      key: 'number',
      label: t('faxDevices:columns.number'),
      sortable: true,
      visible: true,
      render: (_value: unknown, device: FaxDevice) => (
        <span className="font-mono text-primary">{device.number}</span>
      ),
    },
    {
      key: 'type',
      label: t('faxDevices:columns.type'),
      sortable: true,
      visible: true,
      render: (_value: unknown, device: FaxDevice) => {
        const typeColors: Record<string, string> = {
          VIRTUAL: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
          ATA: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
          TRUNK: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
          USER: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
          INBOUND_ROUTE: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400'
        };
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${typeColors[device.type] || 'bg-gray-100 text-gray-800'}`}>
            {t(`faxDevices:types.${device.type}`) || device.type}
          </span>
        );
      }
    },
    {
      key: 'direction',
      label: t('faxDevices:columns.direction'),
      sortable: true,
      visible: true,
      render: (_value: unknown, device: FaxDevice) => {
        const directionIcons: Record<string, string> = {
          inbound: '📥',
          outbound: '📤',
          both: '↔️'
        };
        return (
          <span className="flex items-center gap-2 text-sm">
            <span>{directionIcons[device.direction]}</span>
            <span>{t(`faxDevices:directions.${device.direction}`) || device.direction}</span>
          </span>
        );
      }
    },
    {
      key: 'emailTo',
      label: t('faxDevices:columns.emailTo'),
      sortable: false,
      visible: true,
      render: (_value: unknown, device: FaxDevice) => (
        <span className="text-sm font-mono text-muted-foreground">{device.emailTo || '-'}</span>
      )
    },
    {
      key: 'enabled',
      label: t('faxDevices:columns.enabled'),
      sortable: true,
      visible: true,
      render: (_value: unknown, device: FaxDevice) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${device.enabled
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
            }`}
        >
          {device.enabled ? t('common.enabled') : t('common.disabled')}
        </span>
      )
    },
    {
      key: 'actions',
      label: t('common:actions'),
      sortable: false,
      visible: true,
    },
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useFaxDeviceList({
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10
  });

  // Override columns with our custom renders
  const finalColumns = useMemo(() => {
    return columns.map((col) => {
      const customCol = availableColumns.find(c => c.key === col.key);
      if (customCol && customCol.render) {
        return { ...col, render: customCol.render };
      }
      return col;
    });
  }, [columns, availableColumns]);

  // Handlers
  const handleCreate = useCallback(() => {
    navigate('/pbx/virtual-faxes/devices/new');
  }, [navigate]);

  const handleEdit = useCallback((device: FaxDevice) => {
    navigate(`/pbx/virtual-faxes/devices/${device.uuid ?? device.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((device: FaxDevice) => {
    // TODO: Implement duplicate functionality
    showError('Duplicate functionality not yet implemented');
  }, [showError]);

  const handleDelete = useCallback((device: FaxDevice) => {
    setDeleteModal({ isOpen: true, device });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteModal.device) return;

    try {
      await deleteFaxDevice(String(deleteModal.device.uuid ?? deleteModal.device.id));
      showSuccess(t('faxDevices:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, device: null });
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('faxDevices:messages.deleteError'));
    }
  }, [deleteModal.device, showSuccess, showError, t, refresh]);

  // Render row actions
  const renderActions = useCallback((device: FaxDevice) => (
    <div className="flex gap-1">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(device)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(device)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDelete(device)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [t, handleEdit, handleDuplicate, handleDelete]);

  return (
    <div className="p-6">
      <DataGridSimple
        data={data}
        loading={loading}
        columns={finalColumns}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        renderActions={renderActions}
        searchPlaceholder={t('faxDevices:searchPlaceholder')}
        title={t('faxDevices:title')}
        titleIcon={<Printer className="h-5 w-5 text-primary" />}
        actions={
          <div className="flex items-center gap-2">
            <SimpleTooltip content={t('faxDevices:actions.add')} position="top">
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('faxDevices:actions.add')}
              </button>
            </SimpleTooltip>
          </div>
        }
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, device: null })}
        onConfirm={confirmDelete}
        title={t('faxDevices:messages.deleteTitle')}
        message={t('faxDevices:messages.confirmDelete', { name: deleteModal.device?.name || '' })}
        confirmText={t('faxDevices:actions.delete')}
        cancelText={t('faxDevices:actions.cancel')}
        type="danger"
      />
    </div>
  );
}

